import { Box } from "@mantine/core";
import type {
  FormulaData,
  FormulaTemplateEntry
} from "../ExpressionEditor.types";
import "./ExpressionEditorPreview.scss";

export interface ExpressionEditorPreviewProps {
  value?: FormulaData;
  variables: Array<string>;
  templates?: Array<FormulaTemplateEntry>;
  onClick?: () => void;
}

function ExpressionEditorPreview({
  value,
  variables,
  templates,
  onClick
}: ExpressionEditorPreviewProps) {
  if (!value || !value.formula) {
    return null;
  }

  const template = templates?.find((t) => t.id === value?.template_id);
  const baseFormula = template ? template.formula : value.formula;

  const baseFormulaWithLineBreaks = baseFormula
    .split("\n")
    .flatMap((part, i) => [part, <br key={`line-break-${i}`} />]);

  const partsWithVariables = baseFormulaWithLineBreaks.flatMap((part) =>
    typeof part === "string" ? splitAndWrapVariables(part, variables) : part
  );

  let finalParts = partsWithVariables;

  if (template && template.custom_value_names) {
    finalParts = template.custom_value_names.reduce((acc, customValueName) => {
      const customValueContent = value.custom_values?.[customValueName];
      return acc.flatMap((part) =>
        typeof part === "string" && customValueContent
          ? splitAndWrapCustomValue(part, customValueName, customValueContent)
          : part
      );
    }, partsWithVariables);
  }

  return (
    <Box
      bd="1px solid gray.4"
      className="ExpressionEditorPreview"
      p="md"
      onClick={onClick}
    >
      {finalParts}
    </Box>
  );
}

function splitAndWrapVariables(text: string, variables: Array<string>) {
  if (variables.length === 0) return [text];

  const pattern = variables.sort((a, b) => b.length - a.length).join("|");
  return text.split(new RegExp(`(${pattern})`, "g")).map((part, i) => {
    if (variables.includes(part)) {
      return (
        <span className="expression-variable" key={`${part}-${i}`}>
          {part}
        </span>
      );
    }
    return part;
  });
}

function splitAndWrapCustomValue(
  text: string,
  customValueName: string,
  customValueContent: string
) {
  return text.split(new RegExp(`(${customValueName})`, "g")).map((part, i) => {
    if (part === customValueName) {
      return (
        <span
          className="expression-custom-value"
          key={`${customValueName}-${i}`}
        >
          {customValueContent}
        </span>
      );
    } else {
      return part;
    }
  });
}

export { ExpressionEditorPreview };
